import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { growthApi, growthKeys, type ReferralQuery } from '../api/growth.api';
import type { MerchRequestInput, PhotoType } from '../types';

export function useRewardsSummary() {
  return useQuery({ queryKey: growthKeys.summary(), queryFn: growthApi.getSummary });
}

export function useRewardActivities() {
  return useQuery({ queryKey: growthKeys.activities(), queryFn: growthApi.getActivities });
}

export function useReferrals(query: ReferralQuery) {
  return useQuery({
    queryKey: growthKeys.referrals(query),
    queryFn: () => growthApi.getReferrals(query),
    placeholderData: keepPreviousData,
  });
}

export function useReferral(id: string | null) {
  return useQuery({
    queryKey: growthKeys.referral(id ?? ''),
    queryFn: () => growthApi.getReferral(id!),
    enabled: Boolean(id),
  });
}

export function useCreditTransactions(page: number, limit: number) {
  return useQuery({
    queryKey: growthKeys.transactions(page, limit),
    queryFn: () => growthApi.getTransactions(page, limit),
    placeholderData: keepPreviousData,
  });
}

export function useMerchOptions() {
  return useQuery({ queryKey: growthKeys.merchOptions(), queryFn: growthApi.getMerchOptions });
}

export function useMerchRequests() {
  return useQuery({ queryKey: growthKeys.merchRequests(), queryFn: growthApi.getMerchRequests });
}

export function useRewardPhotos() {
  return useQuery({ queryKey: growthKeys.photos(), queryFn: growthApi.getPhotos });
}

export function useEligibleBookings(enabled: boolean) {
  return useQuery({
    queryKey: growthKeys.eligibleBookings(),
    queryFn: growthApi.getEligibleBookings,
    enabled,
  });
}

/** Any growth write can move several cards at once, so refresh the lot. */
function useInvalidateGrowth() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: growthKeys.all });
}

export function useCreateMerchRequest() {
  const invalidate = useInvalidateGrowth();
  return useMutation({
    mutationFn: (input: MerchRequestInput) => growthApi.createMerchRequest(input),
    onSuccess: invalidate,
  });
}

export function useCancelMerchRequest() {
  const invalidate = useInvalidateGrowth();
  return useMutation({ mutationFn: growthApi.cancelMerchRequest, onSuccess: invalidate });
}

export function useConfirmMerchReceived() {
  const invalidate = useInvalidateGrowth();
  return useMutation({ mutationFn: growthApi.confirmMerchReceived, onSuccess: invalidate });
}

export function useUploadRewardPhoto() {
  const invalidate = useInvalidateGrowth();
  return useMutation({
    mutationFn: (input: { file: File; type: PhotoType; bookingId?: string; consent: boolean }) =>
      growthApi.uploadPhoto(input),
    onSuccess: invalidate,
  });
}
